import { Dispatch, SetStateAction, useState } from "react";
import { fetch } from "@/src/lib/fetch";
import { Button } from "@/src/components/ui/button";
import { ExternalLink } from "lucide-react";
import EditCustomer from "../EditCustomer";
import { CustomerType } from "../model";
import { ColumnDef } from "@tanstack/react-table";

export const getCustomersDataColumns = (
  setShouldRefetch: Dispatch<SetStateAction<boolean>>,
): ColumnDef<CustomerType>[] => [
  {
    header: "Name",
    accessorFn: (r) => (r.firstName ? r.firstName : "-"),
  },
  {
    header: "Surname",
    accessorFn: (r) => (r.lastName ? r.lastName : "-"),
  },
  {
    header: "E-mail",
    accessorFn: (r) => (r.email ? r.email : "-"),
  },
  {
    header: "Additional e-mails",
    accessorFn: (r) => (r.additionalEmails ? r.additionalEmails.map((e) => e.email).join(", ") : "-"),
  },
  {
    header: "Phone number",
    accessorFn: (r) => (r.mobile ? r.mobile : "-"),
  },
  {
    header: "Street address",
    accessorFn: (r) => (r.hubspotAddress?.streetAddress ? r.hubspotAddress?.streetAddress : "-"),
  },
  {
    header: "Postal code",
    accessorFn: (r) => (r.hubspotAddress?.postalCode ? r.hubspotAddress?.postalCode : "-"),
  },
  {
    header: "City",
    accessorFn: (r) => (r.hubspotAddress?.city ? r.hubspotAddress?.city : "-"),
  },
  {
    header: "State/Region",
    accessorFn: (r) => (r.hubspotAddress?.stateRegion ? r.hubspotAddress?.stateRegion : "-"),
  },
  {
    header: "Country/Region",
    accessorFn: (r) => (r.hubspotAddress?.countryRegion ? r.hubspotAddress?.countryRegion : "-"),
  },
  {
    header: "",
    id: "edit",
    cell: ({ row }) => {
      const customer = row.original;
      return (
        <div className="flex items-center gap-2 justify-end">
          <ViewPortalButton email={customer.email} />
          <EditCustomer customer={customer} setShouldRefetch={setShouldRefetch} />
        </div>
      );
    },
  },
];

/* "View portal": mints a short-lived client sign-in link for this customer and
   opens the portal in a new tab, signed in as them — exactly what the client
   sees. Uses the same magic-link mechanics as the sign-in email; nothing is
   emailed and the link expires in 20 minutes. */
const ViewPortalButton = ({ email }: { email?: string | null }) => {
  const [busy, setBusy] = useState(false);
  if (!email) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={busy}
      title="Open the client portal as this customer"
      onClick={async (e) => {
        e.stopPropagation();
        setBusy(true);
        try {
          const res = await fetch<{ url?: string; message?: string }>("/api/users/portal-link", "POST", { email });
          if (res?.url) {
            window.open(res.url, "_blank", "noopener");
          } else {
            alert(res?.message ?? "No portal account for that email");
          }
        } catch {
          alert("Could not create a portal link — the customer may not have an account yet.");
        } finally {
          setBusy(false);
        }
      }}
    >
      <ExternalLink size={14} className="mr-1" />
      {busy ? "Opening…" : "View portal"}
    </Button>
  );
};
